// features/pos/components/ProductCard.tsx
// ENHANCED - Better Visual Feedback, Stock Progress Bar, Quick Preview

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/Badge';
import {
    Package, Tag, Star, AlertTriangle, ShieldX,
    ShoppingCart, Eye, TrendingDown, CheckCircle2
} from 'lucide-react';
import { clsx } from 'clsx';

interface ProductCardProps {
    product: any;
    onSelect: (product: any) => void;
    index: number;
    isBestseller?: boolean;
}

export function ProductCard({ product, onSelect, index, isBestseller }: ProductCardProps) {
    const [isHovered, setIsHovered] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    const isOut = product.isOutOfStock;
    const isLow = product.isLowStock;
    const isUnconfirmed = product.isConfirmed === false;
    const cannotSell = isOut || isUnconfirmed;
    const availableQuantity = parseFloat(product.availableQuantity || '0');

    // Calculate stock percentage for progress bar
    const stockPercentage = availableQuantity > 0
        ? Math.min((availableQuantity / 50) * 100, 100)
        : 0;

    const getStockColor = () => {
        if (availableQuantity === 0) return 'bg-rose-500';
        if (availableQuantity <= 5) return 'bg-amber-500';
        if (availableQuantity <= 10) return 'bg-amber-400';
        return 'bg-emerald-500';
    };

    return (
        <motion.button
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
                delay: Math.min(index * 0.015, 0.3),
                type: 'spring',
                stiffness: 200,
                damping: 20
            }}
            whileHover={!cannotSell ? { scale: 1.03, y: -4 } : {}}
            whileTap={!cannotSell ? { scale: 0.97 } : {}}
            onClick={() => !cannotSell && onSelect(product)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            disabled={cannotSell}
            className={clsx(
                'relative rounded-2xl border-2 p-3 text-left transition-all duration-300 group overflow-hidden',
                'focus:outline-none focus:ring-2 focus:ring-emerald-500/50',
                isOut && 'bg-slate-50 border-slate-200 opacity-50 cursor-not-allowed',
                isUnconfirmed && !isOut && 'bg-orange-50/40 border-orange-300 cursor-not-allowed',
                isLow && !cannotSell && 'bg-amber-50/30 border-amber-300 hover:border-amber-400 hover:shadow-lg hover:shadow-amber-500/10',
                !cannotSell && !isLow && 'bg-white border-slate-200 hover:border-emerald-400 hover:shadow-xl hover:shadow-emerald-500/10'
            )}
            type="button"
        >
            {/* Bestseller Badge */}
            {isBestseller && !cannotSell && (
                <motion.div
                    initial={{ rotate: -5, scale: 0 }}
                    animate={{ rotate: 0, scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                    className="absolute -top-1.5 -left-1.5 z-20"
                >
                    <div className="flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-bold rounded-xl shadow-lg">
                        <Star size={10} className="fill-white" /> Best Seller
                    </div>
                </motion.div>
            )}

            {/* Stock Status Badge */}
            {!cannotSell && (
                <div className="absolute top-2 right-2 z-20">
                    {isLow && (
                        <motion.span
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full shadow-sm flex items-center gap-1"
                        >
                            <TrendingDown size={10} />
                            Low Stock
                        </motion.span>
                    )}
                </div>
            )}

            {/* Unconfirmed Stock Badge */}
            {isUnconfirmed && !isOut && (
                <div className="absolute top-2 right-2 z-20">
                    <span className="px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full shadow-sm flex items-center gap-1">
                        <ShieldX size={10} /> Unverified
                    </span>
                </div>
            )}

            {/* Out of Stock Overlay */}
            {isOut && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm rounded-2xl"
                >
                    <motion.span
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        className="px-4 py-2 bg-rose-500 text-white text-xs font-bold rounded-xl shadow-lg"
                    >
                        Out of Stock
                    </motion.span>
                </motion.div>
            )}

            {/* Unconfirmed Overlay */}
            {isUnconfirmed && !isOut && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 backdrop-blur-sm rounded-2xl"
                >
                    <motion.span
                        initial={{ scale: 0.8 }}
                        animate={{ scale: 1 }}
                        className="px-4 py-2 bg-orange-500 text-white text-xs font-bold rounded-xl shadow-lg flex items-center gap-1.5"
                    >
                        <AlertTriangle size={12} />
                        Stock Unverified
                    </motion.span>
                </motion.div>
            )}

            {/* Product Image */}
            <div className={clsx(
                'aspect-square rounded-xl bg-slate-100 flex items-center justify-center mb-2.5 overflow-hidden relative',
                cannotSell && 'grayscale'
            )}>
                {product.imageUrls?.[0] ? (
                    <>
                        <img
                            src={product.imageUrls[0]}
                            alt={product.name}
                            className={clsx(
                                'w-full h-full object-cover transition-all duration-500',
                                isHovered && !cannotSell && 'scale-110',
                                !imageLoaded && 'opacity-0'
                            )}
                            loading="lazy"
                            onLoad={() => setImageLoaded(true)}
                        />
                        {!imageLoaded && (
                            <div className="absolute inset-0 flex items-center justify-center">
                                <motion.div
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                    className="w-6 h-6 rounded-full border-2 border-slate-300 border-t-emerald-500"
                                />
                            </div>
                        )}
                    </>
                ) : (
                    <motion.div
                        className="text-center"
                        whileHover={!cannotSell ? { scale: 1.1 } : {}}
                    >
                        <Package size={32} className="text-slate-300 mx-auto" />
                        <span className="text-2xl font-bold text-slate-300 mt-1 block">
                            {product.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                    </motion.div>
                )}

                {/* Quick Add Overlay on Hover */}
                {isHovered && !cannotSell && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="absolute inset-0 bg-black/20 flex items-center justify-center rounded-xl"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="bg-white rounded-full p-2 shadow-lg"
                        >
                            <ShoppingCart size={20} className="text-emerald-600" />
                        </motion.div>
                    </motion.div>
                )}
            </div>

            {/* Product Info */}
            <div className="space-y-1">
                <h3 className="text-xs font-semibold text-slate-900 truncate leading-tight group-hover:text-emerald-700 transition-colors">
                    {product.name}
                </h3>
                <p className="text-[10px] text-slate-400 truncate">
                    {product.brand || product.categoryName || '—'}
                </p>
            </div>

            {/* Stock Progress Bar */}
            {!cannotSell && availableQuantity > 0 && (
                <div className="mt-2 mb-1">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[9px] text-slate-400">Stock</span>
                        <span className="text-[9px] font-bold text-slate-500">{availableQuantity} units</span>
                    </div>
                    <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${stockPercentage}%` }}
                            transition={{ duration: 1, ease: 'easeOut' }}
                            className={clsx('h-full rounded-full transition-all', getStockColor())}
                        />
                    </div>
                </div>
            )}

            {/* Price & Actions */}
            <div className="flex items-center justify-between mt-1.5">
                <div>
                    {product.isClearance && product.clearancePrice ? (
                        <div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-sm font-black text-amber-600">
                                    ₦{parseFloat(product.clearancePrice).toLocaleString()}
                                </span>
                                <span className="text-[10px] text-slate-400 line-through">
                                    ₦{parseFloat(product.retailPrice || '0').toLocaleString()}
                                </span>
                            </div>
                            <span className="text-[9px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.2 rounded-full inline-block mt-0.5">
                                Clearance
                            </span>
                        </div>
                    ) : (
                        <div>
                            <p className="text-sm font-bold text-emerald-600">
                                ₦{parseFloat(product.retailPrice || '0').toLocaleString()}
                            </p>
                            {product.wholesalePrice && (
                                <p className="text-[9px] text-slate-400">
                                    Wholesale: ₦{parseFloat(product.wholesalePrice).toLocaleString()}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Stock Indicators */}
                <div className="flex items-center gap-1">
                    {product.isHamper ? (
                        <span className="text-[10px] text-purple-700 font-bold bg-purple-100 px-2 py-0.5 rounded-full flex items-center gap-0.5">
                            🎁 Bundle
                        </span>
                    ) : (
                        <>
                            {isLow && !cannotSell && (
                                <span className="text-[10px] text-amber-600 font-bold bg-amber-100 px-1.5 py-0.5 rounded-full">
                                    {availableQuantity}
                                </span>
                            )}
                            {!cannotSell && !isLow && availableQuantity > 0 && (
                                <CheckCircle2 size={14} className="text-emerald-400" />
                            )}
                        </>
                    )}
                </div>
            </div>
        </motion.button>
    );
}